import { schnorr } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex, hexToBytes, isHex32, isHex64, now, utf8 } from './util.js';
import { getPublicKey, normalizeSecret, normalizePubkey } from './keys.js';

/**
 * NIP-01 compatible event envelope.
 *
 * The event id is the SHA-256 of a canonical JSON serialization; the signature
 * is BIP-340 Schnorr over that id. Keeping this byte-identical to NIP-01 means
 * any Nostr library can verify a Herald event without knowing Herald exists --
 * which is the whole point of not inventing a new wire format.
 */

/** Canonical serialization: [0, pubkey, created_at, kind, tags, content]. */
export function serializeEvent(event) {
  return JSON.stringify([
    0,
    event.pubkey,
    event.created_at,
    event.kind,
    event.tags ?? [],
    event.content ?? '',
  ]);
}

export function getEventHash(event) {
  return bytesToHex(sha256(utf8(serializeEvent(event))));
}

/**
 * Build, hash and sign an event.
 * @param {{kind:number, content?:string, tags?:string[][], created_at?:number}} template
 * @param {Uint8Array|string} secretKey
 */
export function signEvent(template, secretKey) {
  const sk = normalizeSecret(secretKey);
  const unsigned = {
    pubkey: getPublicKey(sk),
    created_at: template.created_at ?? now(),
    kind: template.kind,
    tags: template.tags ?? [],
    content: template.content ?? '',
  };
  const id = getEventHash(unsigned);
  const sig = bytesToHex(schnorr.sign(hexToBytes(id), sk));
  return { ...unsigned, id, sig };
}

/**
 * Structural + cryptographic validation.
 * @returns {{ok:true}|{ok:false, error:string}}
 */
export function checkEvent(event) {
  if (!event || typeof event !== 'object') return { ok: false, error: 'not an object' };
  if (!isHex32(event.id)) return { ok: false, error: 'bad id' };
  if (!isHex32(event.pubkey)) return { ok: false, error: 'bad pubkey' };
  if (!isHex64(event.sig)) return { ok: false, error: 'bad sig' };
  if (!Number.isInteger(event.kind) || event.kind < 0) return { ok: false, error: 'bad kind' };
  if (!Number.isInteger(event.created_at)) return { ok: false, error: 'bad created_at' };
  if (typeof event.content !== 'string') return { ok: false, error: 'bad content' };
  if (!Array.isArray(event.tags)) return { ok: false, error: 'bad tags' };
  for (const tag of event.tags) {
    if (!Array.isArray(tag) || tag.some((v) => typeof v !== 'string')) {
      return { ok: false, error: 'tags must be arrays of strings' };
    }
  }
  if (getEventHash(event) !== event.id) return { ok: false, error: 'id does not match content (tampered)' };
  let sigOk = false;
  try {
    sigOk = schnorr.verify(hexToBytes(event.sig), hexToBytes(event.id), hexToBytes(event.pubkey));
  } catch {
    sigOk = false;
  }
  if (!sigOk) return { ok: false, error: 'invalid signature' };
  return { ok: true };
}

export function verifyEvent(event) {
  return checkEvent(event).ok;
}

/** Throwing variant, for call sites where a bad event is a programming error. */
export function assertEvent(event) {
  const res = checkEvent(event);
  if (!res.ok) throw new Error(`invalid event: ${res.error}`);
  return event;
}

/**
 * Timestamp for the next version of a replaceable or addressable event.
 *
 * NIP-01 resolves a tie on equal `created_at` by lexicographically lower id,
 * which is a coin flip. For an event that carries a *decision* -- a channel's
 * terms, a delegation set, a registry admission -- losing that flip silently
 * discards the newer decision. Anything replacing an existing version must
 * therefore be strictly newer than it, even when both happen inside one second.
 *
 * @param {number|null|undefined} previousCreatedAt
 */
export const MAX_FUTURE_SKEW_SECONDS = 900;

export function nextVersion(previousCreatedAt) {
  const current = now();
  // A previous created_at of 0 is a real timestamp, not an absent one.
  if (previousCreatedAt == null || previousCreatedAt < current) return current;
  // Clamped: a relay that reports an absurdly future previous version must not
  // be able to push our next version past what any relay will accept, which
  // would leave us unable to publish a revocation at all.
  return Math.min(previousCreatedAt + 1, current + MAX_FUTURE_SKEW_SECONDS);
}

/** `naddr`-style coordinate for addressable events: "kind:pubkey:d-tag". */
export function addressOf(event) {
  const d = event.tags.find((t) => t[0] === 'd')?.[1] ?? '';
  return `${event.kind}:${event.pubkey}:${d}`;
}

export function coordinate(kind, pubkey, dTag = '') {
  return `${kind}:${normalizePubkey(pubkey)}:${dTag}`;
}
